/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import DownloadIcon from '@mui/icons-material/Download';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { formatDateOnly } from '@/utils/date';
import { reset } from '../santeMobiliteSlice';
import { downloadSanteMobiliteAttestation } from '../santeMobiliteThunk';
import { resetTempUserInfo } from '@/features/habitation/userSlice';
import { attestationInfoSanteMobiliteApi } from '@/api/santeMobiliteApi';
import { appSuccess } from '@/redux/actions/appActions';
import { authSuccess } from '@/redux/actions/authActions';
import { getDocument } from '@/redux/actions/contractsActions';
import { getMe, createEmailConfirmationStream } from '@/api/authApi';
import LocalStorageService from '@/services/LocalStorageService';
import { trackDatadogAction, trackDatadogError } from '@/services/datadog';
import type { EmailConfirmationMessage } from '@/features/habitation/types';

const Marge = styled.div`
  height: 150px;
`;

const Spacing = styled.div`
  height: 80px;
`;

const ButtonStyled = styled(Button)`
  background-color: #2f6975;
  color: white;
  border-radius: 30px;
  width: 200px;
  height: 40px;
  font-weight: 400;
  font-size: 12px;
  &:hover {
    background-color: #26555f;
  }
  @media (max-width: 600px) {
    width: 100%;
    font-size: 13px;
    height: 30px;
  }
`;

/**
 * Page finale du parcours Santé Mobilité v2.
 *
 * Trois modes :
 *  - free path (inclusion) : ?free=1. L'attestation a été DL au step 5, on
 *    propose juste un re-DL et un retour à l'accueil.
 *  - paid path classique : Stripe → SuccessPaymentPage → (signature OK) →
 *    SellAndSign → cette page. Côté back, le webhook Stripe a déjà généré
 *    l'attestation et l'a envoyée par mail au client. On affiche juste un
 *    message "ton attestation arrive par mail".
 *  - paid path avec signatureError=1 : SuccessPaymentPage a échoué à fetcher
 *    /signature-link après les retries. Le contrat est bien souscrit, on
 *    affiche un message dédié rassurant.
 *
 * Si l'utilisateur est encore en inscription temporaire (fast registered),
 * on attend la validation email via SSE avant d'afficher le merci, comme
 * Habitation/RC.
 */
const SanteMobiliteSuccessPage: React.FC = () => {
  const { t } = useTranslation('sante-mobilite');
  const dispatch = useAppDispatch();
  const isTempRegistered = useAppSelector(state => state.user.isTempRegistered);

  // Snapshot des champs nécessaires au re-DL dans le free path (inclusion).
  const reduxStartDate = useAppSelector(state => state.santeMobilite.startDate);
  const reduxEndDate = useAppSelector(state => state.santeMobilite.endDate);
  const reduxDestination = useAppSelector(state => state.santeMobilite.destination);

  // Mode "free path" : on arrive ici après le téléchargement gratuit de
  // l'attestation inclusion (cf. handleDownloadAttestation dans FinalizationStep).
  const isFreePath = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('free') === '1';
  }, []);

  // contractReference utile uniquement pour la SSE de validation email — on
  // n'appelle plus d'endpoint attestation. Priorité query param, Redux, puis
  // backup localStorage.
  const reduxContractReference = useAppSelector(
    state => state.santeMobilite.contractReference,
  );
  const contractReference = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get('contractReference') ||
      reduxContractReference ||
      localStorage.getItem('sante_mobilite_contractReference')
    );
  }, [reduxContractReference]);

  // Signal renvoyé par SuccessPaymentPage en cas d'échec du fetch /signature-link
  // après les retries.
  const signatureError = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('signatureError') === '1';
  }, []);

  const [checkingValidation, setCheckingValidation] = useState(false);
  const [emailValidated, setEmailValidated] = useState(false);
  const [freePathSnapshot] = useState(() =>
    isFreePath && reduxStartDate && reduxEndDate && reduxDestination
      ? {
          startDate: reduxStartDate,
          endDate: reduxEndDate,
          destination: reduxDestination,
        }
      : null,
  );
  // attestationId / fileName récupérés via /attestation-info pour permettre le
  // DL direct depuis la page (l'attestation est aussi envoyée par mail par le
  // webhook back, mais le DL en clic évite à l'utilisateur de switcher d'app).
  // On utilise le fileName renvoyé par le back tel quel : c'est le même nom
  // que le PDF joint dans le mail, donc l'utilisateur retrouve le même
  // fichier qu'il DL depuis la page ou depuis sa boîte mail.
  // Parcours gratuit : l'étape de finalisation nous transmet directement
  // l'identifiant du document créé. On évite ainsi `/attestation-info/{ref}`,
  // qui ne trouve pas ces contrats — ils sont de type DOCUMENT_CERTIFICATE,
  // l'endpoint ne cherche que les DOCUMENT_CONTRACT (404 vérifié en preprod).
  const [attestationId, setAttestationId] = useState<number | null>(() => {
    const fromUrl = new URLSearchParams(window.location.search).get(
      'attestationId',
    );
    return fromUrl ? Number(fromUrl) || null : null;
  });
  const [attestationFileName, setAttestationFileName] = useState<string | null>(null);

  const eventSourceRef = useRef<EventSource | null>(null);
  const isMountedRef = useRef(true);
  const hasRunRef = useRef(false);
  const attestationFetchedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  /** Cleanup du state + backups localStorage. Appelé quand on a fini sur cette page. */
  const finalizeCleanup = () => {
    localStorage.removeItem('sante_mobilite_contractReference');
    localStorage.removeItem('sante_mobilite_requiresSignature');
    dispatch(reset());
    dispatch(resetTempUserInfo());
    dispatch(appSuccess());
  };

  /**
   * Récupère { attestationId, fileName } via /attestation-info pour permettre
   * le DL direct depuis la page. Le webhook back génère l'attestation après le
   * paiement, donc il peut y avoir un léger délai → polling sur 409
   * (ATTESTATION_PENDING) max 10× × 1.5s.
   *
   * En cas d'échec final (404 / 403 / retries épuisés) : on log et on n'affiche
   * pas le bouton — le message mail reste le fallback de communication.
   */
  const fetchAttestationInfo = async (ref: string, attempt = 0) => {
    if (!isMountedRef.current) return;
    if (attestationFetchedRef.current) return;
    try {
      const res = await attestationInfoSanteMobiliteApi(ref);
      if (!isMountedRef.current) return;
      const id: number | undefined = res.data?.attestationId;
      const name: string | undefined = res.data?.fileName;
      if (typeof id === 'number') {
        attestationFetchedRef.current = true;
        setAttestationId(id);
        if (name) setAttestationFileName(name);
        trackDatadogAction('sante_mobilite_attestation_info_success', {
          contractReference: ref,
          attempt,
          attestationId: id,
        });
      }
    } catch (err: unknown) {
      if (!isMountedRef.current) return;
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 409 && attempt < 10) {
        // ATTESTATION_PENDING : webhook pas encore terminé.
        trackDatadogAction('sante_mobilite_attestation_info_409_retry', {
          contractReference: ref,
          attempt,
        });
        setTimeout(() => fetchAttestationInfo(ref, attempt + 1), 1500);
        return;
      }
      // eslint-disable-next-line no-console
      console.warn(
        'Impossible de récupérer attestation-info (status:',
        status,
        '). Fallback : DL via mail uniquement.',
      );
      trackDatadogError(err as Error, {
        action: 'sante_mobilite_attestation_info_failed',
        contractReference: ref,
        status,
        attempt,
      });
    }
  };

  const handleDownloadAttestation = () => {
    if (!attestationId) return;
    // Le back renvoie systématiquement un fileName non vide dans la réponse
    // 200 ; le `||` est juste une ceinture/bretelles pour ne pas laisser
    // file-saver sans nom de fichier dans le cas dégénéré où l'API renverrait
    // un fileName vide.
    const filename = attestationFileName || 'attestation-sante-mobilite.pdf';
    trackDatadogAction('sante_mobilite_attestation_download_clicked', {
      attestationId,
      filename,
    });
    dispatch(getDocument({ id: attestationId, filename }));
  };

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    // Event clé : on note l'arrivée du user sur la page finale, avec quel
    // contexte (free/paid, fast registered ou pas, signatureError ou pas).
    // C'est l'event pivot pour reconstruire le funnel : combien arrivent
    // ici vs combien commencent / paient / signent.
    trackDatadogAction('sante_mobilite_thank_you_reached', {
      contractReference: contractReference || 'absent',
      isFreePath,
      isTempRegistered,
      signatureError,
    });

    // Free path (inclusion) : pas de SSE. On reset juste le slice pour qu'une
    // future souscription reparte propre.
    //
    // Si l'étape 5 nous a transmis la référence du contrat, on récupère
    // l'identifiant de l'attestation — le MÊME chemin que le parcours payant.
    // Le bouton de cette page devient alors une simple relecture. Sans
    // référence (sessions ouvertes avant ce correctif), on retombe sur
    // l'ancien comportement.
    if (isFreePath) {
      finalizeCleanup();
      return;
    }

    // Paid path : si l'utilisateur est déjà connecté, pas de SSE nécessaire,
    // on reste sur le merci. On déclenche en parallèle la récupération de
    // l'attestationId pour afficher le bouton DL direct.
    if (!isTempRegistered) {
      finalizeCleanup();
      if (contractReference) fetchAttestationInfo(contractReference);
      return;
    }

    // Paid path fast registered : on attend la validation email via SSE avant
    // d'afficher le merci (l'utilisateur doit cliquer sur le lien dans son mail
    // pour activer son compte).
    if (!contractReference) return;
    setCheckingValidation(true);

    eventSourceRef.current = createEmailConfirmationStream(contractReference, {
      onOpen: () => {
        // connexion SSE ouverte
      },
      onMessage: (data: EmailConfirmationMessage) => {
        if (!isMountedRef.current) return;
        if (data.status === 'confirmed' && data.active && data.isVerified) {
          if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
          }

          localStorage.removeItem('accountValidated');

          getMe()
            .then(response => {
              if (!isMountedRef.current) return;
              if (response?.data) {
                dispatch(
                  authSuccess({ data: { user: response.data }, token: null }),
                );
                const service = LocalStorageService.getService();
                service.setUser({ user: response.data });
              }
            })
            .catch(err => {
              // eslint-disable-next-line no-console
              console.error('Erreur mise à jour auth:', err);
            });

          setCheckingValidation(false);
          setEmailValidated(true);
          finalizeCleanup();
          // Une fois l'email validé, on peut récupérer l'attestationId pour
          // proposer le DL direct.
          fetchAttestationInfo(contractReference);
        }
      },
      onError: () => {
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
        // Fallback : check manuel après un délai, comme RC/Habitation.
        // eslint-disable-next-line no-console
        console.warn('SSE échoué, tentative de vérification manuelle...');
        setTimeout(async () => {
          if (!isMountedRef.current) return;
          try {
            const res = await getMe();
            if (res.status === 200 && res.data?.active && res.data?.isVerified) {
              localStorage.removeItem('accountValidated');
              window.location.href =
                window.location.pathname + window.location.search;
            }
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Erreur check manuel:', err);
          }
        }, 2000);
      },
    });
  }, [contractReference, isTempRegistered, dispatch, isFreePath]);

  // Re-télécharge l'attestation en free path (l'utilisateur peut en avoir
  // besoin une seconde fois). On ré-utilise les valeurs snapshottées au mount.
  const handleRedownloadFree = () => {
    // Relecture par identifiant dès qu'on l'a : aucun contrat créé.
    if (attestationId) {
      handleDownloadAttestation();
      return;
    }
    // Repli pour les sessions ouvertes avant ce correctif, qui n'ont pas de
    // référence en URL. Ce chemin-là recrée un contrat — c'est le défaut
    // d'origine, conservé uniquement pour ne pas laisser ces utilisateurs
    // sans attestation.
    if (!freePathSnapshot) return;
    dispatch(
      downloadSanteMobiliteAttestation({
        startDate: formatDateOnly(freePathSnapshot.startDate),
        endDate: formatDateOnly(freePathSnapshot.endDate),
        country: freePathSnapshot.destination,
      }),
    );
  };

  // En free path on considère "validé" dès l'arrivée puisqu'il n'y a pas de
  // mécanisme de validation email à attendre.
  const showThanks = isFreePath || !isTempRegistered || emailValidated;

  return (
    <>
      <Marge />
      <Box
        sx={{
          maxWidth: 700,
          mx: 'auto',
          p: 2,
          mt: 2,
          position: 'relative',
        }}
      >
        {showThanks && (
          <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
            <Button
              variant="outlined"
              startIcon={<HomeIcon />}
              onClick={() => (window.location.href = '/home')}
              sx={{
                borderColor: '#2f6975',
                color: '#2f6975',
                '&:hover': {
                  borderColor: '#26555f',
                  backgroundColor: 'rgba(47, 105, 117, 0.04)',
                },
              }}
            >
              {t('stepfinal.back_to_home')}
            </Button>
          </Box>
        )}

        <Grid container>
          <Grid item xs={12} sm={12} textAlign="center">
            <Box
              component="img"
              src="/images/logos/logo-blue.png"
              alt="logo"
              sx={{
                height: { xs: 64, sm: 90 },
                width: { xs: 42, sm: 52 },
                mx: { xs: 'auto', sm: 0 },
              }}
            />
          </Grid>
          <Spacing />
          <Grid item xs={12} sm={12} textAlign="center">
            <Typography variant="h3">
              {t('stepfinal.subscribe_success')}
            </Typography>
          </Grid>

          {showThanks && (
            <>
              <Spacing />
              <Grid item xs={12} sm={12} textAlign="center">
                <Typography variant="h1">
                  {t('stepfinal.subscribe_thanks')}
                </Typography>
              </Grid>
              <Spacing />
            </>
          )}

          {!isFreePath && isTempRegistered && checkingValidation && !emailValidated && (
            <Grid item xs={12} sm={12} textAlign="center" mt={4}>
              <CircularProgress color="primary" />
              <Box mt={2}>
                <Typography variant="h5">
                  {t('stepfinal.waiting_validation')}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('stepfinal.email_sent_check')}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Paid path nominal : le webhook back a généré et envoyé
              l'attestation par mail. On affiche en plus un bouton DL direct
              dès que /attestation-info a répondu, pour éviter la friction
              "j'ouvre ma boîte mail" surtout sur mobile. */}
          {!isFreePath && showThanks && !signatureError && (
            <Grid item xs={12} sm={12} mt={2} display="flex" justifyContent="center">
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  maxWidth: 560,
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    background: '#e3f2fd',
                    borderRadius: 2,
                    px: 3,
                    py: 2,
                    boxShadow: 1,
                    textAlign: 'left',
                    width: '100%',
                  }}
                >
                  <EmailOutlinedIcon color="primary" sx={{ fontSize: 32 }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      {t('stepfinal.attestation_by_mail_title')}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, color: '#37474f' }}>
                      {t('stepfinal.attestation_by_mail_subtitle')}
                    </Typography>
                  </Box>
                </Box>

                {attestationId ? (
                  <ButtonStyled
                    type="button"
                    onClick={handleDownloadAttestation}
                    startIcon={<DownloadIcon />}
                    aria-label={t('stepfinal.download_attestation')}
                    sx={{ width: { xs: '100%', sm: 280 } }}
                  >
                    {t('stepfinal.download_attestation')}
                  </ButtonStyled>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: '#607d8b',
                    }}
                  >
                    <CircularProgress size={18} color="inherit" />
                    <Typography variant="body2">
                      {t('stepfinal.preparing_attestation')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          )}

          {/* Fallback affiché si SuccessPaymentPage a échoué sur le fetch
              /signature-link après les retries. Le contrat est bien souscrit,
              la signature et l'attestation arriveront par email. */}
          {!isFreePath && signatureError && (
            <Grid item xs={12} sm={12} textAlign="center" mt={4}>
              <Typography variant="h5" sx={{ color: '#c62828' }}>
                {t('stepfinal.signature_error_title')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t('stepfinal.signature_error_subtitle')}
              </Typography>
            </Grid>
          )}

          {/* Free path : l'attestation a été DL côté front depuis FinalizationStep.
              On propose de la re-télécharger en cas de besoin. */}
          {isFreePath && freePathSnapshot && (
            <Grid item xs={12} sm={12} textAlign="center" mt={2}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  background: '#e3f2fd',
                  borderRadius: 2,
                  px: 3,
                  py: 2,
                  mt: 2,
                  boxShadow: 1,
                }}
              >
                <InsertDriveFileIcon color="primary" sx={{ fontSize: 32 }} />
                <ButtonStyled
                  type="button"
                  onClick={handleRedownloadFree}
                  style={{
                    fontWeight: 600,
                    color: '#1976d2',
                    textDecoration: 'none',
                    fontSize: 12,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                  aria-label={t('stepfinal.download_attestation')}
                >
                  {t('stepfinal.download_attestation')}
                </ButtonStyled>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
      <Marge />
    </>
  );
};

export default SanteMobiliteSuccessPage;
